import { state } from '@angular/animations'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { map, pairwise, tap } from 'rxjs/operators'
import { NotificationService } from 'src/app/modules/commons/components/notification/services/notification.service'
import { FilterUid } from 'src/app/modules/commons/models/filters.model'
import { SearchFiltersConvertorService } from 'src/app/modules/search/services/search-filters-convertor.service'
import { MachineStateComputed } from '../../models/states-computed.models'
import { LoadStatus, State, TagRecord } from '../../models/states.models'
import { TouaregControlDisplayService } from '../../services/touareg-control-display.service'
import { DisplayState } from '../../services/touareg-control-display.service'
import {
  HealthMonitorData,
  TouaregControlStateHealthMonitorService,
} from '../../services/touareg-control-state-health-monitor.service'
import { TouaregControlStateMachineService } from '../../services/touareg-control-state-machine.service'
import { TouaregControlStateParamsService } from '../../services/touareg-control-state-params.service'
import { TouaregControlDialogComponent } from '../touareg-control-dialog/touareg-control-dialog.component'
import { Samv2Module } from '@aubert-et-duval/samv2';

@Component({
  selector: 'app-touareg-control-trigger',
  templateUrl: './touareg-control-trigger.component.html',
  styleUrls: ['./touareg-control-trigger.component.scss'],
  imports: [Samv2Module],
})
export class TouaregControlTriggerComponent implements OnInit, OnDestroy {
  private _touaregStatesSubs$!: Subscription
  private _touaregHealthSubs$!: Subscription
  private _touaregParamsSubs$!: Subscription
  private _touaregDisplaySubs$!: Subscription
  private _closeSubs$!: Subscription
  private _dialogRef: MatDialogRef<TouaregControlDialogComponent> | null = null

  public health!: HealthMonitorData
  public touaregConnected!: boolean
  public displayState!: string
  public warning!: boolean
  public dropNb: number | null = null
  public active!: boolean
  public beaconId: any
  public connect!: boolean

  private touaregControlDisplay: DisplayState | undefined

  get isOnline(): boolean {
    return (
      this.health &&
      !this.health.tooMuchLatency &&
      this.health.status === 'connected'
    )
  }

  get isSettingsOk(): boolean {
    return !!this.beaconId && this.connect
  }

  get isActive(): boolean {
    return this.active && this.health?.active
  }

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _stateMachine: TouaregControlStateMachineService,
    private _params: TouaregControlStateParamsService,
    private _monitor: TouaregControlStateHealthMonitorService,
    private _dialog: MatDialog,
    private _display: TouaregControlDisplayService,
    private translateService: TranslateService,
    private _notify: NotificationService //private _filterConvertor: SearchFiltersConvertorService,
  ) {}

  ngOnInit(): void {
    // On écoute l'état machine
    this._touaregStatesSubs$ = this._stateMachine.stateMachine$
      .pipe(
        pairwise(),
        tap((states) => this._checkAutoShow(states)),
        map((states) => states[1])
      )
      .subscribe((state) => this._storeState(state))

    // On écoute le health monitor
    this._touaregHealthSubs$ = this._monitor.health$.subscribe((health) => {
      this.health = health
    })

    // On écoute les params
    this._touaregParamsSubs$ = this._params.params$.subscribe((params) => {
      const { beaconId, active, connect } = params
      this.active = active
      this.beaconId = beaconId
      this.connect = connect
    })

    // On écoute l'état d'affichage
    this._touaregDisplaySubs$ = this._display.display$.subscribe((display) => {
      this.touaregControlDisplay = display
    })

    // On écoute les demande de cloture du touareg UI
    this._closeSubs$ = this._display.closeUI$.subscribe((close) => {
      if (close) {
        this.closeDialog()
      }
    })
  }

  private _checkAutoShow(states: (MachineStateComputed | null)[]) {
    const {
      pickAutoShow,
      dropAutoShow,
      dropAlertAlgo,
      dropAlertAlgoForce,
      afterDropFocusMap,
    } = this._params.settings
    const stateChange = states[0]?.state !== states[1]?.state

    // Si connexion perdue, on n'automatise pas le comportement de la fenetre
    if (this.health?.tooMuchLatency) {
      console.log('Abort ---> too much latency...')
      return
    }

    // On filtre la map sur les derniers drops
    if (this._checkIsDrop(states)) {
      // On supprime toute la notification d'attente du résultat du drop
      // if (this._display.notificationToRemove) {
      //   this._notify.dismissNotification(this._display.notificationToRemove)
      //   this._display.setNotificationToRemove(null)
      // }
      // On récupère les tags droppés
      // Si les settings filter on map sont activés, on filtre également sur Explorer

      const droppedTags = this._getDroppedTags(states[1]!)

      // Si des tags on étét droppés
      if (droppedTags?.length) {
        // check if we are in the research menu and if 'afterDropFocusMap' is activated
        if (
          afterDropFocusMap &&
          this._router.url.split('?')[0] === '/search'
        ) {
          // add queryParamsValue
          let queryParamsvalue: string = ''
          droppedTags.forEach((tag) => {
            queryParamsvalue += tag.tag_uid + ','
          })
          queryParamsvalue = queryParamsvalue.slice(0, -1)
          const queryParams = { tft: queryParamsvalue }

          // redirect to actual root with queryParams
          this._router.navigate(['/search'], {
            queryParams,
            relativeTo: this._route,
          })
        }
        this._notify.pushSuccess(
          `Mahvis : ${droppedTags.length} ${this.translateService.instant(
            'touareg.tagsDrop'
          )}`
        )
      }
    }

    // Si on est en ouverture manuelle, on n'automatise pas le comportement de la fenetre
    if (this.touaregControlDisplay?.openByUser === true) {
      return
    }

    // On écoute si un warning est levé pairingWarningList
    const warning = states[1]?.computed.warningList?.length
    const warningPairings = states[1]?.computed.pairingWarningList?.length

    // On prend en compte le force auto-show en cas de warning
    const showPick = pickAutoShow ? true : warningPairings ? true : false
    const showDrop = dropAutoShow
      ? true
      : (warning && dropAlertAlgo && dropAlertAlgoForce) || warningPairings
      ? true
      : false

    // Si autoshow est activé
    if (showPick || showDrop) {
      // Si on est en IDLE, on ferme
      if (
        states[1]?.state === State.IDLE &&
        this.touaregControlDisplay?.currentState === 'state'
      ) {
        this.closeDialog()
        return
      }

      // Si on est entrain de pick, en autoshow,
      // et que les settings des picks sont en hidden, on clos.
      if (
        !showPick &&
        (states[1]?.state === State.PICKING_SCANNING ||
          states[1]?.state === State.PICKED)
      ) {
        this.closeDialog()
        return
      }

      // Si on est entrain de drop, en autoshow,
      // et que les settings des drop sont en hidden, on clos.
      if (
        !showDrop &&
        (states[1]?.state === State.DROP_SCANNING ||
          states[1]?.state === State.DROP_ACCEPTING_USER_CHOICE)
      ) {
        this.closeDialog()
        return
      }

      // Sinon, on ouvre !
      if (stateChange) {
        this.openDialog()
      }
    }
  }

  private _checkIsDrop(states: (MachineStateComputed | null)[]): boolean {
    // Si pas de state à comparer, pas de drop
    if (!states[0] || !states[1]) {
      return false
    }
    // Si pas de changement de session, pas de drop
    if (
      states[0].state_machine_session_uid ===
      states[1].state_machine_session_uid
    ) {
      return false
    }
    // On considère qu'il y a un drop si le last_session uid
    // est égal au session uid de l'état précédent
    return (
      states[0].state_machine_session_uid ===
      states[1].last_session?.state_machine_session_uid
    )
  }

  private _storeState(state: MachineStateComputed | null) {
    this.warning =
      !!state?.computed?.warningList?.length ||
      !!state?.computed?.pairingWarningList?.length

    switch (state?.state) {
      case State.IDLE:
        this.displayState = 'idle'
        this.dropNb = null
        break

      case State.PICKING_SCANNING:
        this.displayState = 'pick-scan'
        this.dropNb = this._getPickNumber(state)
        break

      case State.PICKED:
        this.displayState = 'drop-forward'
        this.dropNb = this._getPickNumber(state)
        break

      case State.DROP_SCANNING:
        this.displayState = 'drop-scan'
        this.dropNb = this._getPickNumber(state)
        break

      case State.DROP_ACCEPTING_USER_CHOICE:
      case State.DROP_BLOCKED_BY_USER:
        this.displayState = 'drop'
        this.dropNb = this._getPickNumber(state)
        break
    }
  }

  private _getPickNumber(state: MachineStateComputed): number | null {
    let count = null
    if (state?.tags_list) {
      count = state.tags_list.filter(
        (t) => t.load_status === LoadStatus.loaded
      ).length
    }
    return count
  }

  private _getDroppedTags(state: MachineStateComputed): TagRecord[] {
    const tags = state.last_session?.tags_list || ([] as TagRecord[])
    return tags.filter((t) => t.load_status === LoadStatus.loaded)
  }

  private _filterMapToLastDrop(tags: TagRecord[]) {
    // On créé les filtres
    const tagsFilters = tags.map((t) => {
      return new FilterUid({ value: t.tag_uid as string })
    })

    // On filtre sur Explorer
    //const queryParams = this._filterConvertor.filtersToParams(tagsFilters)
    //this._router.navigate(['/geolocation'], { queryParams })
  }

  public manualClickTrigger() {
    if (!this.isSettingsOk) {
      // si problème dans les settings
      // alors que la fonctionnnalité est active
      // on renvoie ver sla page de settings
      this._router.navigate(['mahvis', 'settings'])
    } else {
      // sinon, on ouvre la modale du controller
      this._display.enterManualTouaregControllerOn()
      this.openDialog()
    }
  }

  public openDialog(): void {
    // Si une modale est deja ouverte, on stoppe
    if (this._dialogRef) return

    // Ouverture de la modale
    this._dialogRef = this._dialog.open(TouaregControlDialogComponent, {
      width: '1200px',
      height: '100%',
      // maxHeight: 'calc(100% - 10px)',
      panelClass: 'touareg-control-pane',
      backdropClass: 'touarteg-control-backdrop',
    })

    // On reset la reference apres un close
    this._dialogRef.afterClosed().subscribe(() => {
      this._display.enterManualTouaregControllerOff()
      this._dialogRef = null
    })
  }

  public closeDialog(filters?: FilterUid[]) {
    if (!this._dialogRef) return
    this._dialogRef.close()
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    if (!!this._touaregStatesSubs$) this._touaregStatesSubs$.unsubscribe()
    if (!!this._touaregHealthSubs$) this._touaregHealthSubs$.unsubscribe()
    if (!!this._touaregParamsSubs$) this._touaregParamsSubs$.unsubscribe()
    if (!!this._touaregDisplaySubs$) this._touaregDisplaySubs$.unsubscribe()
    if (!!this._closeSubs$) this._closeSubs$.unsubscribe()
  }
}
